import Typography from "@material-ui/core/Typography";
import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import CampaignCreationSlideContainer from "../../../components/CampaignCreation/CampaignCreationSlideContainer";
import ConfirmationModal from "../../../components/modules/ConfirmationModal";
import Loader from "../../../components/elements/Loader";
import SuccessDialog from "./SuccessDialog";
import classes from "./CampaignSummarySlide.module.scss";
import { BaseStepComponentProps } from "../../../components/modules/BaseStepper/BaseStepper";
import { CampaignType } from "../../../types/campaignTypes";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { resetCampaignToCreateState } from "../../../redux/reducers/campaignToCreate";

const CampaignSummarySlide: FC<BaseStepComponentProps> = ({
  handleResetSteps,
  isAttemptingToGoNext,
  setIsAttemptingToGoNext,
  setIsNextButtonDisabled,
  campaignAction,
}) => {
  const campaignToCreate = useSelector((state: ReduxApplicationState) => state.campaignToCreate);
  const isPending = useSelector((state: ReduxApplicationState) => state.campaignToCreate.isPending);
  const [pixelEventId, setPixelEventId] = useState<number | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

  const dispatch = useDispatch();
  const history = useHistory();

  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);

  const handleCloseSuccessDialog = () => {
    setIsSuccessDialogOpen(false);
    setIsNextButtonDisabled!(false);
    handleResetSteps!();
    dispatch(resetCampaignToCreateState());
    history.push("/");
  };

  const handleCloseConfirmationModal = () => setIsConfirmModalOpen(false);

  const handleCreateCampaign = () => {
    const onCampaignCreated = (createdCampaign: CampaignType) => {
      setPixelEventId(createdCampaign.pixel_event_id);
      setIsSuccessDialogOpen(true);
    };
    dispatch(campaignAction(onCampaignCreated));
    setIsNextButtonDisabled!(true);
  };

  useEffect(() => {
    if (isAttemptingToGoNext) {
      setIsConfirmModalOpen(true);
      setIsAttemptingToGoNext(false);
    }
  }, [isAttemptingToGoNext, setIsAttemptingToGoNext]);

  return (
    <CampaignCreationSlideContainer heading="Campaign Summary">
      <div className={classes.container}>
        {isPending && <Loader />}

        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          handleClose={handleCloseConfirmationModal}
          confirmQuestion="Create Campaign?"
          confirmButtonLabel="Yes"
          handleConfirm={handleCreateCampaign}
        />

        <SuccessDialog
          isOpen={isSuccessDialogOpen}
          handleClose={handleCloseSuccessDialog}
          pixelEventId={pixelEventId}
        />
        <div className={classes.campaignSummary}>
          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Advertiser Name:</Typography>
            <Typography className={classes.fieldValue}>{campaignToCreate.selectedAdvertiser?.name}</Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Created By:</Typography>
            <Typography className={classes.fieldValue}>{campaignToCreate.selectedAdvertiser?.createdBy}</Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Campaign Name:</Typography>
            <Typography className={classes.fieldValue}>{campaignToCreate.basicDetails.name}</Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Campaign Description:</Typography>
            <Typography className={classes.fieldValue}>{campaignToCreate.basicDetails.description ?? ""}</Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Campaign Start Date:</Typography>
            <Typography className={classes.fieldValue}>
              {new Date(Date.parse(campaignToCreate.basicDetails.startDate as string)).toLocaleDateString()}
            </Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Campaign End Date:</Typography>
            <Typography className={classes.fieldValue}>
              {new Date(Date.parse(campaignToCreate.basicDetails.endDate as string)).toLocaleDateString()}
            </Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Daily Budget:</Typography>
            <Typography className={classes.fieldValue}>$ {campaignToCreate.basicDetails.dailyBudget}</Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Estimated Budget:</Typography>
            <Typography className={classes.fieldValue}>$ {campaignToCreate.basicDetails.estimatedBudget}</Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Campaign Max Bid:</Typography>
            <Typography className={classes.fieldValue}>$ {campaignToCreate.basicDetails.maxBid}</Typography>
          </div>

          <div className={classes.dataRow}>
            <Typography className={classes.fieldLabel}>Campaign Landing Page:</Typography>
            <a href={campaignToCreate.landingPage} target="_blank" rel="noopener noreferrer">
              {campaignToCreate.landingPage}
            </a>
          </div>
        </div>
      </div>
    </CampaignCreationSlideContainer>
  );
};

export default CampaignSummarySlide;
